import React, {
  forwardRef, useState, useEffect,
} from 'react';

import { ButtonSwitch, ButtonSwitchGroup } from '@vlabs/uikit';

import type { PopoverWithButtonProps } from '@vlabs/monokit/shared/ui/popovers';
import { PopoverWithButton } from '@vlabs/monokit/shared/ui/popovers';

import { GEOLOCATION_FIELDS, hasLocationFilters } from './geolocation';
import { Tabs } from './tabs';
import { useEventListFiltersTranslation } from '../../i18n';
import { useEventListFilters } from '../../useEventListFilters';
import st from '../popovers.module.sass';

type TTabs = 'map' | 'address' | 'coordinates';

export interface LocationFilterPopoverProps extends PopoverWithButtonProps {
  includedAttributes?: string[];
}

function LocationFilterPopover(
  {
    includedAttributes = [],
    ...props
  }: LocationFilterPopoverProps,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const { t } = useEventListFiltersTranslation();
  const { filters, updateFilters } = useEventListFilters();

  const [activeTab, setActiveTab] = useState<TTabs>('address');

  const isMapEnabled = includedAttributes.includes('map');

  useEffect(() => {
    setActiveTab(isMapEnabled ? 'map' : 'address');
  }, [isMapEnabled]);

  const reset = () => updateFilters(
    Object.fromEntries(GEOLOCATION_FIELDS.map((field) => [field, undefined])),
  );

  return (
    <PopoverWithButton
      {...props}
      className={st.LocationFilterPopover}
      isButtonDisabled={!hasLocationFilters(filters)}
      onClickReset={reset}
      ref={ref}
    >
      <ButtonSwitchGroup
        data-testid="geolocationFiltersSwitch"
        onChange={setActiveTab}
        size="small"
        value={activeTab}
        variant="square"
      >
        {!!isMapEnabled && (
          <ButtonSwitch value="map">
            {t('location_tab_map')}
          </ButtonSwitch>
        )}
        <ButtonSwitch value="address">
          {t('location_tab_address')}
        </ButtonSwitch>
        <ButtonSwitch value="coordinates">
          {t('location_tab_coordinates')}
        </ButtonSwitch>
      </ButtonSwitchGroup>
      {activeTab === 'map' && <Tabs.MapTab />}
      {activeTab === 'coordinates' && <Tabs.CoordinatesTab />}
      {activeTab === 'address' && <Tabs.AddressTab />}
    </PopoverWithButton>
  );
}

const LocationFilterPopoverWithRef = forwardRef(LocationFilterPopover);
export { LocationFilterPopoverWithRef as LocationFilterPopover };
